import { styled } from '@linaria/react';
import { Loader, Menu } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useRef } from 'react';
import {
  getInstallStartUrl,
  getUserAuthStartUrl,
} from '../../../../api/slack-integrations-client/slack-integrations-client';
import {
  useDisconnectSlackUser,
  useSlackStatus,
  useUninstallSlack,
} from '../../../../api/slack-integrations-client/slack-integrations-client.hooks';
import {
  getInstallStartUrl as getTeamsInstallStartUrl,
  getUserAuthStartUrl as getTeamsUserAuthStartUrl,
} from '../../../../api/teams-integrations-client/teams-integrations-client';
import {
  useDisconnectTeamsUser,
  useTeamsStatus,
  useUninstallTeams,
} from '../../../../api/teams-integrations-client/teams-integrations-client.hooks';
import { getUserAuthStartUrl as getGoogleUserAuthStartUrl } from '../../../../api/google-integrations-client/google-integrations-client';
import {
  useDisconnectGoogle,
  useGoogleStatus,
} from '../../../../api/google-integrations-client/google-integrations-client.hooks';
import iconSlack from '../../../../assets/icons/iconSlack.svg';
import iconTeams from '../../../../assets/icons/iconTeams.svg';
import { UserContext } from '../../../../contexts';
import { useRoles } from '../../../../helpers/auth-helpers/auth.hooks';
import { useFeatureFlag } from '../../../../helpers/feature-flags/feature-flags.hooks';
import { FeatureFlag } from '../../../../helpers/feature-flags/feature-flags.type';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { Badge } from '../../../../ui-library/badge/badge';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { GoogleGLogo } from '../agent-customize/brand-icons';
import { FloraConnectionId } from '../agent-settings/agent-settings.type';
import { AgentViewHeader } from '../agent-view-header';
import { openOAuthPopup } from './agent-integrations.helpers';
import { useOAuthPopupAutoClose } from './agent-integrations.hooks';

interface AgentIntegrationsViewProps {
  /** Render inside the settings layout: no page heading, padding, or scrolling. */
  embedded?: boolean;
  /** Render only this connection's section (a connection's settings page). All sections when omitted. */
  only?: FloraConnectionId;
}

/**
 * Lists the Slack, Microsoft Teams, and Google connections with their connect,
 * disconnect, and (admin) uninstall actions. Slack and Teams are each behind
 * their own feature flag.
 *
 * @param {AgentIntegrationsViewProps} props - Embedding mode and an optional single connection to show
 * @returns {JSX.Element} The connections list
 */
export function AgentIntegrationsView({ embedded = false, only }: AgentIntegrationsViewProps = {}) {
  const shows = (id: FloraConnectionId) => !only || only === id;
  const queryClient = useQueryClient();
  const { user, query: userQuery } = useContext(UserContext);
  const { isAdmin } = useRoles();
  const organization = useGlobalStore((state) => state.organization);
  const isEnabled = useFeatureFlag(FeatureFlag.FloraSlackIntegration);
  const isTeamsEnabled = useFeatureFlag(FeatureFlag.FloraTeamsIntegration);

  const { data: status, query: statusQuery } = useSlackStatus(isEnabled);
  const { mutate: uninstall, isPending: isUninstalling } = useUninstallSlack();
  const { mutate: disconnect, isPending: isDisconnecting } = useDisconnectSlackUser();

  const { data: teamsStatus, query: teamsStatusQuery } = useTeamsStatus(isTeamsEnabled);
  const { mutate: teamsUninstall, isPending: isTeamsUninstalling } = useUninstallTeams();
  const { mutate: teamsDisconnect, isPending: isTeamsDisconnecting } = useDisconnectTeamsUser();

  const { data: googleStatus, query: googleStatusQuery } = useGoogleStatus();
  const { mutate: googleDisconnect, isPending: isGoogleDisconnecting } = useDisconnectGoogle();

  const pollTimerRef = useRef<number | null>(null);

  useOAuthPopupAutoClose();

  useEffect(() => {
    // Stop the Google popup poll if the user navigates away while it's still
    // open, so the interval doesn't leak and refetch from a stale closure.
    return () => {
      if (pollTimerRef.current !== null) {
        window.clearInterval(pollTimerRef.current);
      }
    };
  }, []);

  // Each OAuth popup closes itself once the provider is done (Slack/Teams on a
  // server page, Google back on the legacy route). Poll for that so status is
  // refetched the moment the user finishes; one popup at a time shares the timer.
  const watchPopupUntilClosed = (popup: Window, onClosed: () => void) => {
    if (pollTimerRef.current !== null) {
      window.clearInterval(pollTimerRef.current);
    }
    pollTimerRef.current = window.setInterval(() => {
      if (!popup.closed) return;
      if (pollTimerRef.current !== null) {
        window.clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      onClosed();
    }, 500);
  };

  const startOAuthFlow = async (fetchUrl: (organizationId: string) => Promise<string>, onClosed: () => void) => {
    if (!organization?.id) return;
    const popup = openOAuthPopup();
    if (!popup) {
      notifications.show({
        message: 'Popup blocked. Please allow popups for this site and try again.',
        color: 'red',
      });
      return;
    }
    try {
      const url = await fetchUrl(organization.id);
      popup.location.href = url;
      watchPopupUntilClosed(popup, onClosed);
    } catch (error) {
      popup.close();
      const message = error instanceof Error ? error.message : 'Failed to start the connection';
      notifications.show({ message, color: 'red' });
    }
  };

  const handleInstall = () => {
    void startOAuthFlow(getInstallStartUrl, () => void statusQuery.refetch());
  };

  const handleConnectUser = () => {
    void startOAuthFlow(getUserAuthStartUrl, () => void statusQuery.refetch());
  };

  const handleUninstall = () => {
    if (
      !window.confirm(
        'Uninstall Slack? Flora will stop responding in Slack channels and DMs until the workspace is reconnected.',
      )
    )
      return;
    uninstall(undefined, {
      onSuccess: () => notifications.show({ message: 'Slack uninstalled', color: 'green' }),
      onError: (error) => notifications.show({ message: error.message, color: 'red' }),
    });
  };

  const handleDisconnect = () => {
    disconnect(undefined, {
      onSuccess: () => notifications.show({ message: 'Slack account disconnected', color: 'green' }),
      onError: (error) => notifications.show({ message: error.message, color: 'red' }),
    });
  };

  const handleTeamsInstall = () => {
    void startOAuthFlow(getTeamsInstallStartUrl, () => void teamsStatusQuery.refetch());
  };

  const handleTeamsConnectUser = () => {
    void startOAuthFlow(getTeamsUserAuthStartUrl, () => void teamsStatusQuery.refetch());
  };

  const handleTeamsUninstall = () => {
    if (
      !window.confirm(
        'Uninstall Microsoft Teams? Flora will stop responding in Teams chats and channels until the tenant is reconnected.',
      )
    )
      return;
    teamsUninstall(undefined, {
      onSuccess: () => notifications.show({ message: 'Microsoft Teams uninstalled', color: 'green' }),
      onError: (error) => notifications.show({ message: error.message, color: 'red' }),
    });
  };

  const handleTeamsDisconnect = () => {
    teamsDisconnect(undefined, {
      onSuccess: () => notifications.show({ message: 'Microsoft Teams account disconnected', color: 'green' }),
      onError: (error) => notifications.show({ message: error.message, color: 'red' }),
    });
  };

  const handleGoogleConnect = async () => {
    if (!organization?.id) return;
    const popup = openOAuthPopup();
    if (!popup) {
      notifications.show({
        message: 'Popup blocked. Please allow popups for this site and try again.',
        color: 'red',
      });
      return;
    }
    try {
      // Authenticated start endpoint: binds the OAuth exchange to the session
      // user so the callback saves credentials to whoever clicked Connect
      // (the Google account email may differ from the login email).
      const authorizeUrl = await getGoogleUserAuthStartUrl(organization.id);
      popup.location.href = authorizeUrl;
    } catch (error) {
      popup.close();
      const message = error instanceof Error ? error.message : 'Failed to start the Google connection';
      notifications.show({ message, color: 'red' });
      return;
    }
    watchPopupUntilClosed(popup, () => {
      void googleStatusQuery.refetch();
      // The capability catalog carries the Google bucket's connection state.
      void queryClient.invalidateQueries({ queryKey: ['flora-capabilities'] });
    });
  };

  const handleGoogleDisconnect = () => {
    googleDisconnect(undefined, {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['flora-capabilities'] });
        notifications.show({ message: 'Google account disconnected', color: 'green' });
      },
      onError: (error) => notifications.show({ message: error.message, color: 'red' }),
    });
  };

  const isRefreshing =
    (statusQuery.isFetching && !statusQuery.isLoading) ||
    (isTeamsEnabled && teamsStatusQuery.isFetching && !teamsStatusQuery.isLoading) ||
    (googleStatusQuery.isFetching && !googleStatusQuery.isLoading);

  const refreshButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={() => {
        statusQuery.refetch();
        if (isTeamsEnabled) teamsStatusQuery.refetch();
        googleStatusQuery.refetch();
      }}
      loading={isRefreshing}
      aria-label="Refresh integrations status"
    >
      <Icon name="refresh" color="var(--ink-dark)" size={16} style={{ marginRight: 4, cursor: 'pointer' }} />
      Refresh
    </Button>
  );

  // When embedded for one connection, another integration's status doesn't hold
  // this panel in a loading or error state.
  const showsSlack = isEnabled && shows(FloraConnectionId.Slack);
  const showsTeams = isTeamsEnabled && shows(FloraConnectionId.Teams);
  const showsGoogle = shows(FloraConnectionId.Google);

  if (
    (showsSlack && statusQuery.isLoading) ||
    (showsTeams && teamsStatusQuery.isLoading) ||
    (showsGoogle && googleStatusQuery.isLoading) ||
    userQuery.isLoading ||
    (!user && !userQuery.isError)
  ) {
    return (
      <Container data-embedded={embedded || undefined}>
        <CenterWrap>
          {embedded ? (
            <EmbeddedActions>{refreshButton}</EmbeddedActions>
          ) : (
            <AgentViewHeader title="Integrations" rightSlot={refreshButton} />
          )}
          <Content>
            <LoadingState>
              <Loader size="sm" color="var(--secondary-base)" />
            </LoadingState>
          </Content>
        </CenterWrap>
      </Container>
    );
  }

  if (
    (showsSlack && statusQuery.isError) ||
    (showsTeams && teamsStatusQuery.isError) ||
    (showsGoogle && googleStatusQuery.isError) ||
    userQuery.isError
  ) {
    return (
      <Container data-embedded={embedded || undefined}>
        <CenterWrap>
          {embedded ? (
            <EmbeddedActions>{refreshButton}</EmbeddedActions>
          ) : (
            <AgentViewHeader title="Integrations" rightSlot={refreshButton} />
          )}
          <Content>
            <EmptyState>
              <Text size="regular" color="var(--ink-lighter)">
                Failed to load integrations status.
              </Text>
            </EmptyState>
          </Content>
        </CenterWrap>
      </Container>
    );
  }

  const installed = !!status?.installed;
  const userConnected = !!status?.user_connected;
  const installDisabledReason = !isAdmin
    ? 'Only organization admins can install Slack.'
    : !organization?.id
      ? 'Select an organization before connecting Slack.'
      : '';

  const teamsInstalled = !!teamsStatus?.installed;
  const teamsUserConnected = !!teamsStatus?.user_connected;
  const teamsInstallDisabledReason = !isAdmin
    ? 'Only organization admins can install Microsoft Teams.'
    : !organization?.id
      ? 'Select an organization before connecting Microsoft Teams.'
      : '';

  const googleConnected = !!googleStatus?.connected;
  const googleEmail = googleStatus?.google_email ?? null;

  return (
    <Container data-embedded={embedded || undefined}>
      <CenterWrap>
        {embedded ? (
          <EmbeddedActions>{refreshButton}</EmbeddedActions>
        ) : (
          <AgentViewHeader
            title="Integrations"
            subtitle="Connect Flora to your team's tools."
            rightSlot={refreshButton}
          />
        )}

        <Content>
          <FormContent>
            {isEnabled && shows(FloraConnectionId.Slack) && (
              <Section>
                <SectionHeader>
                  <SlackIconWrap>
                    <SlackLogo src={iconSlack} alt="Slack" />
                  </SlackIconWrap>
                  <SectionTitleBlock>
                    <Text size="regular" weight="bold" color="var(--ink-darkest)">
                      Slack
                    </Text>
                    <Text size="small" color="var(--ink-lighter)">
                      Mention @Flora in channels, DM Flora directly.
                    </Text>
                  </SectionTitleBlock>

                  <RightActions>
                    {!installed && (
                      <Tooltip
                        label={installDisabledReason}
                        variant="black"
                        disabled={!installDisabledReason}
                        position="bottom"
                        withArrow
                      >
                        <TooltipTarget tabIndex={installDisabledReason ? 0 : undefined}>
                          <Button variant="primary" onClick={handleInstall} disabled={!!installDisabledReason}>
                            Connect
                          </Button>
                        </TooltipTarget>
                      </Tooltip>
                    )}
                    {installed && (
                      <>
                        <Badge tone="success" icon="check_circle" size="md">
                          Connected
                        </Badge>
                        {isAdmin && (
                          <Menu shadow="sm" radius="md" position="bottom-end" offset={4} width={180}>
                            <Menu.Target>
                              <IconButton type="button" aria-label="Slack workspace actions" disabled={isUninstalling}>
                                <Icon name="more_vert" color="var(--ink-lighter)" size={20} />
                              </IconButton>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                color="red"
                                leftSection={<Icon name="link_off" color="var(--danger-base)" size={16} />}
                                onClick={handleUninstall}
                                disabled={isUninstalling}
                              >
                                <Text size="small">Uninstall Slack</Text>
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        )}
                      </>
                    )}
                  </RightActions>
                </SectionHeader>

                {installed && (
                  <SubSection>
                    <SubSectionHeader>
                      <SubSectionTitleBlock>
                        <Text size="small" weight="bold" color="var(--ink-darkest)">
                          Your Slack account
                        </Text>
                        <Text size="small" color="var(--ink-lighter)">
                          Sign in with your Slack identity, so Flora can list the channels and DMs you have access to
                          when you schedule deliveries.
                        </Text>
                        <ScopeList>
                          <ScopeItem>
                            <ScopeName>channels:read</ScopeName>
                            <Text size="small" color="var(--ink-lighter)">
                              See public channels you can already see in Slack.
                            </Text>
                          </ScopeItem>
                          <ScopeItem>
                            <ScopeName>groups:read</ScopeName>
                            <Text size="small" color="var(--ink-lighter)">
                              See private channels you are a member of.
                            </Text>
                          </ScopeItem>
                          <ScopeItem>
                            <ScopeName>users:read</ScopeName>
                            <Text size="small" color="var(--ink-lighter)">
                              Match your Slack profile to your Bloomfilter account.
                            </Text>
                          </ScopeItem>
                        </ScopeList>
                        <Text size="tiny" color="var(--ink-lighter)">
                          Flora never reads message content with these scopes — only channel and member metadata.
                        </Text>
                      </SubSectionTitleBlock>

                      <RightActions>
                        {!userConnected && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={handleConnectUser}
                            disabled={!organization?.id}
                          >
                            Connect
                          </Button>
                        )}
                        {userConnected && (
                          <>
                            <Badge tone="success" icon="check_circle" size="md">
                              Connected
                            </Badge>
                            <Menu shadow="sm" radius="md" position="bottom-end" offset={4} width={180}>
                              <Menu.Target>
                                <IconButton type="button" aria-label="Slack account actions" disabled={isDisconnecting}>
                                  <Icon name="more_vert" color="var(--ink-lighter)" size={20} />
                                </IconButton>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Item
                                  color="red"
                                  leftSection={<Icon name="link_off" color="var(--danger-base)" size={16} />}
                                  onClick={handleDisconnect}
                                  disabled={isDisconnecting}
                                >
                                  <Text size="small">Disconnect account</Text>
                                </Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          </>
                        )}
                      </RightActions>
                    </SubSectionHeader>
                  </SubSection>
                )}
              </Section>
            )}

            {isTeamsEnabled && shows(FloraConnectionId.Teams) && (
              <Section>
                <SectionHeader>
                  <SlackIconWrap>
                    <SlackLogo src={iconTeams} alt="Microsoft Teams" />
                  </SlackIconWrap>
                  <SectionTitleBlock>
                    <Text size="regular" weight="bold" color="var(--ink-darkest)">
                      Microsoft Teams
                    </Text>
                    <Text size="small" color="var(--ink-lighter)">
                      Mention @Flora in chats and channels.
                    </Text>
                  </SectionTitleBlock>

                  <RightActions>
                    {!teamsInstalled && (
                      <Tooltip
                        label={teamsInstallDisabledReason}
                        variant="black"
                        disabled={!teamsInstallDisabledReason}
                        position="bottom"
                        withArrow
                      >
                        <TooltipTarget tabIndex={teamsInstallDisabledReason ? 0 : undefined}>
                          <Button
                            variant="primary"
                            onClick={handleTeamsInstall}
                            disabled={!!teamsInstallDisabledReason}
                          >
                            Connect
                          </Button>
                        </TooltipTarget>
                      </Tooltip>
                    )}
                    {teamsInstalled && (
                      <>
                        <Badge tone="success" icon="check_circle" size="md">
                          Connected
                        </Badge>
                        {isAdmin && (
                          <Menu shadow="sm" radius="md" position="bottom-end" offset={4} width={180}>
                            <Menu.Target>
                              <IconButton
                                type="button"
                                aria-label="Microsoft Teams actions"
                                disabled={isTeamsUninstalling}
                              >
                                <Icon name="more_vert" color="var(--ink-lighter)" size={20} />
                              </IconButton>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                color="red"
                                leftSection={<Icon name="link_off" color="var(--danger-base)" size={16} />}
                                onClick={handleTeamsUninstall}
                                disabled={isTeamsUninstalling}
                              >
                                <Text size="small">Uninstall Microsoft Teams</Text>
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        )}
                      </>
                    )}
                  </RightActions>
                </SectionHeader>

                {teamsInstalled && (
                  <SubSection>
                    <SubSectionHeader>
                      <SubSectionTitleBlock>
                        <Text size="small" weight="bold" color="var(--ink-darkest)">
                          Your Microsoft Teams account
                        </Text>
                        <Text size="small" color="var(--ink-lighter)">
                          Sign in with your Microsoft identity, so Flora can list the teams and channels you have access
                          to when you schedule deliveries.
                        </Text>
                        <ScopeList>
                          <ScopeItem>
                            <ScopeName>
                              {
                                /* grammar-check disable-next-line */
                                'Team.ReadBasic.All'
                              }
                            </ScopeName>
                            <Text size="small" color="var(--ink-lighter)">
                              See the teams you are a member of.
                            </Text>
                          </ScopeItem>
                          <ScopeItem>
                            <ScopeName>
                              {
                                /* grammar-check disable-next-line */
                                'Channel.ReadBasic.All'
                              }
                            </ScopeName>
                            <Text size="small" color="var(--ink-lighter)">
                              See channels within those teams.
                            </Text>
                          </ScopeItem>
                          <ScopeItem>
                            <ScopeName>
                              {
                                /* grammar-check disable-next-line */
                                'User.Read'
                              }
                            </ScopeName>
                            <Text size="small" color="var(--ink-lighter)">
                              Match your Microsoft profile to your Bloomfilter account.
                            </Text>
                          </ScopeItem>
                        </ScopeList>
                        <Text size="tiny" color="var(--ink-lighter)">
                          Flora never reads message content with these scopes — only team and channel metadata.
                        </Text>
                      </SubSectionTitleBlock>

                      <RightActions>
                        {!teamsUserConnected && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={handleTeamsConnectUser}
                            disabled={!organization?.id}
                          >
                            Connect
                          </Button>
                        )}
                        {teamsUserConnected && (
                          <>
                            <Badge tone="success" icon="check_circle" size="md">
                              Connected
                            </Badge>
                            <Menu shadow="sm" radius="md" position="bottom-end" offset={4} width={180}>
                              <Menu.Target>
                                <IconButton
                                  type="button"
                                  aria-label="Microsoft Teams account actions"
                                  disabled={isTeamsDisconnecting}
                                >
                                  <Icon name="more_vert" color="var(--ink-lighter)" size={20} />
                                </IconButton>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Item
                                  color="red"
                                  leftSection={<Icon name="link_off" color="var(--danger-base)" size={16} />}
                                  onClick={handleTeamsDisconnect}
                                  disabled={isTeamsDisconnecting}
                                >
                                  <Text size="small">Disconnect account</Text>
                                </Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          </>
                        )}
                      </RightActions>
                    </SubSectionHeader>
                  </SubSection>
                )}
              </Section>
            )}

            {shows(FloraConnectionId.Google) && (
              <Section>
                <SectionHeader>
                  <SlackIconWrap>
                    <GoogleGLogo />
                  </SlackIconWrap>
                  <SectionTitleBlock>
                    <Text size="regular" weight="bold" color="var(--ink-darkest)">
                      Google
                    </Text>
                    <Text size="small" color="var(--ink-lighter)">
                      Let Flora create and update files in your Google Drive.
                    </Text>
                    {googleConnected && googleEmail && (
                      <Text size="small" color="var(--ink-lighter)">
                        Connected as {googleEmail}
                      </Text>
                    )}
                  </SectionTitleBlock>

                  <RightActions>
                    {!googleConnected && (
                      <Button variant="primary" onClick={() => void handleGoogleConnect()} disabled={!organization?.id}>
                        Connect
                      </Button>
                    )}
                    {googleConnected && (
                      <>
                        <Badge tone="success" icon="check_circle" size="md">
                          Connected
                        </Badge>
                        <Menu shadow="sm" radius="md" position="bottom-end" offset={4} width={180}>
                          <Menu.Target>
                            <IconButton
                              type="button"
                              aria-label="Google account actions"
                              disabled={isGoogleDisconnecting}
                            >
                              <Icon name="more_vert" color="var(--ink-lighter)" size={20} />
                            </IconButton>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              color="red"
                              leftSection={<Icon name="link_off" color="var(--danger-base)" size={16} />}
                              onClick={handleGoogleDisconnect}
                              disabled={isGoogleDisconnecting}
                            >
                              <Text size="small">Disconnect</Text>
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </>
                    )}
                  </RightActions>
                </SectionHeader>

                <SubSection>
                  <SubSectionHeader>
                    <SubSectionTitleBlock>
                      <Text size="small" weight="bold" color="var(--ink-darkest)">
                        What Flora can access
                      </Text>
                      <ScopeList>
                        <ScopeItem>
                          <ScopeName>drive.file</ScopeName>
                          <Text size="small" color="var(--ink-lighter)">
                            Create and edit Google Drive files Flora makes for you, or that you open for Flora.
                          </Text>
                        </ScopeItem>
                      </ScopeList>
                      <Text size="tiny" color="var(--ink-lighter)">
                        Flora cannot access other files in your Drive.
                      </Text>
                    </SubSectionTitleBlock>
                  </SubSectionHeader>
                </SubSection>
              </Section>
            )}
          </FormContent>
        </Content>
      </CenterWrap>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  padding: 40px 0;
  overflow-y: auto;

  &[data-embedded] {
    height: auto;
    padding: 0;
    overflow: visible;
  }
`;

const EmbeddedActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
`;

const CenterWrap = styled.div`
  width: 100%;
`;

const Content = styled.div`
  flex: 1;
  padding: 0 80px;

  [data-embedded] & {
    padding: 0;
  }
`;

const FormContent = styled.div`
  width: 100%;
  max-width: 720px;
  margin: 0 auto;

  [data-embedded] & {
    max-width: none;
    margin: 0;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
`;

const Section = styled.div`
  margin-bottom: 8px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
`;

const SlackIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: var(--sky-lightest);
  flex-shrink: 0;
`;

const SlackLogo = styled.img`
  width: 24px;
  height: 24px;
`;

const SectionTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const TooltipTarget = styled.span`
  display: inline-flex;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background: var(--sky-lightest);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const SubSection = styled.div`
  margin: 16px 0 0 60px;
  padding: 16px 20px;
  background: var(--sky-lightest);
  border-radius: 10px;
`;

const SubSectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const SubSectionTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const ScopeList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 4px 0;
  padding: 0;
  list-style: none;
`;

const ScopeItem = styled.li`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const ScopeName = styled.code`
  flex-shrink: 0;
  padding: 1px 6px;
  background: var(--white);
  border: 1px solid var(--sky-light);
  border-radius: 4px;
  font-family: monospace;
  font-size: 11px;
  color: var(--ink-dark);
`;

export type { AgentIntegrationsViewProps };
